import {
  Button,
  FlexBox,
  JoonUIColor,
  Loader,
  SPACING,
  TextArea,
  Typography,
} from "@joonapp/web-shared"
import { MenuItem } from "@mui/material"
import { useState } from "react"
import toast from "react-hot-toast"

import MarkdownResultCard from "./MarkdownResultCard"
import {
  copyText,
  trackGenerationCompleted,
  trackGenerationFailed,
  trackGenerationStarted,
  trackResultCopied,
} from "./sharedTools"
import ToolPage from "./ToolPage"
import JoonSelectInput from "../../components/inputs/JoonSelectInput"
import { generateBipTemplate } from "../../networking/tools"
import { requireAuth } from "../../util/auth"

const TOOL_ID = "bip_template_generator"

const BipTemplateGenerator = requireAuth(() => {
  const [targetBehaviors, setTargetBehaviors] = useState("")
  const [functionOfBehavior, setFunctionOfBehavior] = useState("")
  const [clientInfo, setClientInfo] = useState("")
  const [currentInterventions, setCurrentInterventions] = useState("")
  const [environmentalFactors, setEnvironmentalFactors] = useState("")
  const [bip, setBip] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const generate = async () => {
    setLoading(true)
    setError(null)
    const t0 = Date.now()
    trackGenerationStarted(TOOL_ID, {
      has_function: !!functionOfBehavior,
      has_client_info: !!clientInfo,
      has_current_interventions: !!currentInterventions,
      has_environmental_factors: !!environmentalFactors,
    })
    try {
      const data = await generateBipTemplate({
        targetBehaviors,
        functionOfBehavior,
        clientInfo: clientInfo || "Not provided",
        currentInterventions: currentInterventions || "Not provided",
        environmentalFactors: environmentalFactors || "Not provided",
      })
      setBip(data.bipTemplate)
      trackGenerationCompleted(TOOL_ID, { duration_ms: Date.now() - t0 })
    } catch (e: any) {
      setError("Failed to generate BIP template. Please try again.")
      trackGenerationFailed(TOOL_ID, { error_code: e.message })
    }
    setLoading(false)
  }

  const onCopy = async () => {
    await copyText(bip)
    toast.success("Copied to clipboard")
    trackResultCopied(TOOL_ID)
  }

  return (
    <ToolPage
      toolId={TOOL_ID}
      title="BIP Template Generator"
      description="Build a comprehensive Behavior Intervention Plan template tailored to a target behavior."
    >
      <Field label="Target behavior (required)">
        <TextArea
          name="Target behavior"
          value={targetBehaviors}
          onChange={(e) => setTargetBehaviors(e.target.value)}
          fullWidth
        />
      </Field>
      <Field label="Function of behavior (required)">
        <JoonSelectInput
          value={functionOfBehavior}
          setValue={setFunctionOfBehavior}
          placeholder="Select function"
        >
          <MenuItem value="attention">Attention</MenuItem>
          <MenuItem value="escape">Escape</MenuItem>
          <MenuItem value="access">Access to tangibles</MenuItem>
          <MenuItem value="sensory">Sensory</MenuItem>
        </JoonSelectInput>
      </Field>
      <Field label="Client information (optional)">
        <TextArea
          name="Client information"
          value={clientInfo}
          onChange={(e) => setClientInfo(e.target.value)}
          fullWidth
        />
      </Field>
      <Field label="Current interventions (optional)">
        <TextArea
          name="Current interventions"
          value={currentInterventions}
          onChange={(e) => setCurrentInterventions(e.target.value)}
          fullWidth
        />
      </Field>
      <Field label="Environmental factors (optional)">
        <TextArea
          name="Environmental factors"
          value={environmentalFactors}
          onChange={(e) => setEnvironmentalFactors(e.target.value)}
          fullWidth
        />
      </Field>
      <Button
        text="Generate BIP template"
        onClick={generate}
        disabled={loading || !targetBehaviors || !functionOfBehavior}
        fullWidth
      />
      {loading && (
        <FlexBox justify="center">
          <Loader size={24} color={JoonUIColor.semantic.primary} />
        </FlexBox>
      )}
      {error && (
        <Typography variant="bodySmall" color={JoonUIColor.semantic.alert}>
          {error}
        </Typography>
      )}
      {bip && (
        <MarkdownResultCard
          title="Generated BIP template"
          markdown={bip}
          onCopy={onCopy}
        />
      )}
    </ToolPage>
  )
})

const Field = ({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) => (
  <FlexBox direction="column" gap={SPACING.space1}>
    <Typography variant="bodyBold" textAlign="left">
      {label}
    </Typography>
    {children}
  </FlexBox>
)

export default BipTemplateGenerator
